import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { reportUnexpectedError } from '../../../lib/reportError'
import { isDeviceForbiddenError } from '../../../lib/deviceAuthError'

export interface RestoreRewardInput {
  memberId: string
  pin: string
  rewardItemId: string
  restoreCount: number
  reason: string
  note: string
}

export interface RestoreRewardResult {
  newCount: number
  rewardName: string
}

interface RestoreRewardResponse {
  success?: boolean
  error?: string
  newCount?: number
  rewardName?: string
}

export class InvalidManagerPinError extends Error {
  constructor() {
    super('invalid_pin')
  }
}

async function restoreReward(input: RestoreRewardInput): Promise<RestoreRewardResult> {
  const { data, error } = await supabase.functions.invoke<RestoreRewardResponse>('restore-reward', {
    body: {
      memberId:     input.memberId,
      pin:          input.pin,
      rewardItemId: input.rewardItemId,
      restoreCount: input.restoreCount,
      reason:       input.reason,
      note:         input.note || undefined,
    },
  })

  if (data?.error === 'invalid_pin') throw new InvalidManagerPinError()
  if (error || !data?.success) {
    const failure = error ?? new Error('restore_reward_failed')
    if (!isDeviceForbiddenError(failure)) reportUnexpectedError(failure)
    throw failure
  }

  return {
    newCount:   data.newCount!,
    rewardName: data.rewardName!,
  }
}

export function useRestoreReward() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: restoreReward,
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['member-stamp-data', variables.memberId] })
      void queryClient.invalidateQueries({ queryKey: ['member-reward-data', variables.memberId] })
    },
  })
}